import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Avatar } from '@/components/ui/Avatar';
import { useAuth } from '@/stores/auth';
import { useUsRing } from './state';
import { useWatchExtension } from './watch/extension';

const RING_MS = 60_000;

/**
 * "Maya is waiting in your room" (SPEC_V15 §3.4): shown anywhere in the app
 * (inside or outside Us) with Join and Not now; gone after a minute.
 */
export function UsRingHost() {
  const ring = useUsRing((s) => s.ring);
  const clear = () => useUsRing.getState().set(null);
  const navigate = useNavigate();
  const loc = useLocation();
  const authed = useAuth((s) => s.status === 'authed');
  const meId = useAuth((s) => s.me?.id);
  const ext = useWatchExtension();
  // "Come watch": with Popin Watch the party is in the extension, not a Popin page.
  const inExtension = ring?.to === 'watch' && !!ext.version && ext.linked?.user_id === meId;
  const target = ring?.to === 'watch' ? '/us/watch' : '/us/call';
  const here = loc.pathname === target && !inExtension;

  useEffect(() => {
    if (!ring) return;
    const t = setTimeout(clear, Math.max(0, ring.at + RING_MS - Date.now()));
    return () => clearTimeout(t);
  }, [ring]);
  useEffect(() => {
    if (here) clear();
  }, [here]);

  return (
    <AnimatePresence>
      {ring && authed && !here && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed inset-x-0 top-[calc(12px+env(safe-area-inset-top))] z-[90] flex justify-center px-4"
          role="alertdialog"
          aria-label={`${ring.from.display_name} is waiting for you`}
          data-testid="us-ring"
        >
          <div className="flex w-full max-w-md items-center gap-3 rounded-card bg-night p-3 pl-4 text-bone shadow-lift">
            <Avatar avatar={ring.from.avatar} size={44} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold">
                {inExtension ? `${ring.from.display_name} wants to watch with you` : `${ring.from.display_name} is in your room`}
              </p>
              <p className="truncate text-[13px] opacity-75">{inExtension ? 'In Popin Watch, just the two of you' : ring.to === 'watch' ? 'Come watch together' : 'Join the video call'}</p>
            </div>
            <button type="button" onClick={clear} className="h-10 rounded-full px-3 text-[14px] font-medium opacity-80">Not now</button>
            <button
              type="button"
              onClick={() => {
                clear();
                if (inExtension) ext.join();
                else navigate(target, { state: { join: true } });
              }}
              className="h-10 rounded-full bg-green px-4 text-[14px] font-semibold text-night"
              data-testid="us-ring-join"
            >
              Join
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
